import React from 'react';
import { alpha, Box, Divider, Stack, Typography } from '@mui/material';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import VolunteerActivismRoundedIcon from '@mui/icons-material/VolunteerActivismRounded';
import LocalGasStationRoundedIcon from '@mui/icons-material/LocalGasStationRounded';

import loginImage1 from '../assets/login/Login Image 1.png';
import loginImage2 from '../assets/login/Login Image 2.png';
import loginImage3 from '../assets/login/Login Image 3.png';
import loginImage4 from '../assets/login/Login Image 4.png';
import loginImage5 from '../assets/login/Login Image 5.png';
import loginImage6 from '../assets/login/Login Image 6.png';
import loginImage7 from '../assets/login/Login Image 7.png';

export const LOGIN_GREEN = '#07872b';
export const LOGIN_LIME = '#72d72d';
export const LOGIN_INK = '#10151d';

const pictures = [
  { src: loginImage2, alt: 'Valli Forecourts SoftCare car wash', position: '50% 50%', size: 'auto 84%' },
  { src: loginImage3, alt: 'Valli Forecourts fuel pumps', position: '50% 50%', size: 'auto 84%' },
  { src: loginImage4, alt: 'Valli Forecourts shop interior', position: '50% 50%', size: '84% auto' },
  { src: loginImage5, alt: 'Valli Forecourts site entrance', position: '50% 50%', size: '84% auto' },
  { src: loginImage6, alt: 'Valli Forecourts illuminated canopy', position: '50% 50%', size: '84% auto' },
  { src: loginImage7, alt: 'Valli Forecourts jet wash', position: '50% 50%', size: '84% auto' },
];

function CompositePicture({ picture, index }) {
  const lean = -9.25;
  const rowOffset = index >= 4 ? -45 : index >= 2 ? -23 : 0;
  return (
    <Box
      role="figure"
      aria-label={picture.alt}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1.52',
        overflow: 'hidden',
        borderRadius: '12px',
        border: `1.75px solid ${LOGIN_LIME}`,
        boxShadow: '0 9px 20px rgba(0,0,0,.32)',
        bgcolor: '#07170d',
        transform: `translateX(${rowOffset}px) skewX(${lean}deg)`,
        transformOrigin: 'center',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: '-8%',
          backgroundImage: `url("${picture.src}")`,
          backgroundSize: 'cover',
          backgroundPosition: picture.position,
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#06140c',
          transform: `skewX(${-lean}deg) rotate(2deg)`,
          transformOrigin: 'center',
        }}
      />
    </Box>
  );
}

const defaultFeatures = [
  {
    icon: InsightsRoundedIcon,
    title: 'Smarter operations',
    text: 'Real-time insights and powerful tools for better site performance.',
  },
  {
    icon: SecurityRoundedIcon,
    title: 'Secure & reliable',
    text: 'Enterprise-grade security to protect your data and operations.',
  },
  {
    icon: GroupsOutlinedIcon,
    title: 'Built for everyone',
    text: 'Designed for site teams, managers and directors.',
  },
];

const goldenFeatures = [
  {
    icon: BarChartRoundedIcon,
    title: 'Product performance',
    text: 'Compare weekly quantity, sales, margin and profit across every site.',
  },
  {
    icon: SecurityRoundedIcon,
    title: 'Controlled access',
    text: 'Separate manager permissions keep Golden Lines reporting protected.',
  },
  {
    icon: GroupsOutlinedIcon,
    title: 'One company view',
    text: 'Understand reporting coverage and product results across the network.',
  },
];

export function ValliMark({ golden = false, compact = false, dark = false }) {
  return (
    <Stack direction="row" spacing={compact ? 1 : 1.35} alignItems="center" sx={{ width: 'fit-content' }}>
      <Box
        sx={{
          width: compact ? 46 : 58,
          height: compact ? 46 : 58,
          border: `2px solid ${dark ? LOGIN_GREEN : '#8ef05a'}`,
          borderRadius: compact ? 2.2 : 2.7,
          display: 'grid',
          placeItems: 'center',
          color: dark ? LOGIN_GREEN : '#9df26d',
          background: dark
            ? 'linear-gradient(145deg, #ffffff, #edf8f0)'
            : 'linear-gradient(145deg, rgba(8,117,48,.92), rgba(1,39,20,.82))',
          boxShadow: dark
            ? `0 8px 20px ${alpha(LOGIN_GREEN, 0.14)}, inset 0 0 0 1px rgba(255,255,255,.8)`
            : '0 12px 28px rgba(0,0,0,.3), inset 0 0 0 1px rgba(255,255,255,.14)',
          flex: '0 0 auto',
          transform: 'rotate(-4deg)',
        }}
      >
        <LocalGasStationRoundedIcon sx={{ fontSize: compact ? 27 : 35, transform: 'rotate(4deg)' }} />
      </Box>
      <Box>
        <Typography
          sx={{
            color: dark ? LOGIN_INK : 'white',
            fontWeight: 950,
            fontSize: compact ? 20 : 27,
            lineHeight: 0.95,
            letterSpacing: compact ? 1.7 : 2.6,
            whiteSpace: 'nowrap',
          }}
        >
          VALLI
        </Typography>
        <Typography
          sx={{
            color: dark ? LOGIN_GREEN : LOGIN_LIME,
            fontWeight: 950,
            fontSize: compact ? 11.5 : 11.5,
            lineHeight: 1.1,
            letterSpacing: compact ? 1.5 : 2.25,
            mt: 0.55,
            whiteSpace: 'nowrap',
          }}
        >
          {golden ? 'GOLDEN LINES · VALLI' : 'FORECOURTS LIMITED'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function ValliLoginShowcase({ golden = false }) {
  const features = golden ? goldenFeatures : defaultFeatures;
  return (
    <Box
      component="section"
      aria-label="Valli Forecourts"
      sx={{
        minHeight: '100dvh',
        position: 'relative',
        overflow: 'hidden',
        color: 'white',
        display: { xs: 'none', lg: 'block' },
        clipPath: 'polygon(0 0, 100% 0, 91% 100%, 0 100%)',
        bgcolor: '#03150c',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#02140b' }} />
      <Box
        component="img"
        src={loginImage1}
        alt="Full Valli Forecourts BP and SPAR site"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 'auto',
          objectFit: 'fill',
          filter: 'saturate(1.04) contrast(1.02)',
          opacity: 1,
          transform: 'none',
          transformOrigin: 'top left',
          WebkitMaskImage: 'linear-gradient(180deg, #000 0%, #000 67%, rgba(0,0,0,.94) 73%, rgba(0,0,0,.76) 80%, rgba(0,0,0,.46) 89%, transparent 100%)',
          maskImage: 'linear-gradient(180deg, #000 0%, #000 67%, rgba(0,0,0,.94) 73%, rgba(0,0,0,.76) 80%, rgba(0,0,0,.46) 89%, transparent 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 30% 58%, rgba(22,139,70,.1) 0%, rgba(8,70,36,.04) 31%, transparent 57%),
            linear-gradient(90deg, rgba(0,18,10,.2) 0%, rgba(0,18,10,.05) 54%, transparent 84%),
            linear-gradient(
              180deg,
              transparent 0%,
              transparent 34%,
              rgba(0,18,10,.035) 40%,
              rgba(0,22,11,.1) 46%,
              rgba(0,25,13,.22) 54%,
              rgba(0,27,14,.42) 65%,
              rgba(0,24,12,.66) 79%,
              rgba(0,18,10,.88) 100%
            )
          `,
          backdropFilter: 'saturate(.94)',
        }}
      />
      <Stack sx={{ position: 'relative', zIndex: 2, minHeight: '100dvh', p: { lg: 4, xl: 5 }, pr: { lg: 8, xl: 11 } }}>
        <Box sx={{ transform: 'translateY(-16px)', width: 'fit-content' }}>
          <ValliMark golden={golden} />
        </Box>

        <Box sx={{ position: 'absolute', left: { lg: 40, xl: 50 }, bottom: { lg: 118, xl: 130 }, maxWidth: 390 }}>
          <Typography sx={{ fontSize: { lg: 40, xl: 48 }, lineHeight: 0.98, fontWeight: 950, letterSpacing: -1.2 }}>
            {golden ? 'PRODUCTS THAT' : 'FUELLING YOUR'}
          </Typography>
          <Typography sx={{ mt: 0.7, color: LOGIN_LIME, fontSize: { lg: 40, xl: 48 }, lineHeight: 0.98, fontWeight: 950, letterSpacing: -1.2 }}>
            {golden ? 'MATTER' : 'JOURNEY'}
          </Typography>
          <Typography sx={{ mt: 1.7, fontSize: 18, color: 'rgba(255,255,255,.9)' }}>
            {golden ? 'Performance you can see' : 'Every mile, every time'}
          </Typography>

          <Stack spacing={1.7} sx={{ mt: 3 }}>
            {features.map(({ icon: Icon, title, text }) => (
              <Stack key={title} direction="row" spacing={1.4} alignItems="flex-start">
                <Box sx={{ width: 43, height: 43, flex: '0 0 auto', borderRadius: 2.2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,91,36,.78)' }}>
                  <Icon sx={{ color: '#d7ffc7' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: 13.5, textTransform: 'uppercase' }}>{title}</Typography>
                  <Typography sx={{ mt: 0.2, maxWidth: 285, color: 'rgba(255,255,255,.78)', fontSize: 12.5, lineHeight: 1.4 }}>{text}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box sx={{
          position: 'absolute',
          top: '48.4%',
          right: { lg: 66, xl: 80 },
          width: 'clamp(440px, 23vw, 440px)',
          display: { lg: 'grid' },
          gridTemplateColumns: '2fr 2fr',
          gap: { lg: 0.5, xl: 0.65 },
          transform: 'rotate(-0.5deg)',
          transformOrigin: 'top center',
        }}>
          {pictures.map((picture, index) => <CompositePicture key={picture.src} picture={picture} index={index} />)}
        </Box>

        <Stack direction="row" divider={<Divider orientation="vertical" flexItem sx={{ borderColor: alpha(LOGIN_LIME, 0.38) }} />}
          sx={{ position: 'absolute', left: { lg: 38, xl: 48 }, bottom: { lg: 24, xl: 30 }, width: 'fit-content', minWidth: { lg: 385, xl: 445 }, px: { lg: 1.25, xl: 1.5 }, py: { lg: 1.05, xl: 1.2 }, borderRadius: 2.6, border: `1.25px solid ${alpha(LOGIN_LIME, 0.8)}`, bgcolor: 'rgba(1,34,17,.96)', boxShadow: '0 12px 28px rgba(0,0,0,.25)', backdropFilter: 'blur(12px)' }}>
          {[
            { icon: AccessTimeRoundedIcon, value: '24/7', label: 'Operations' },
            { icon: StorefrontRoundedIcon, value: 'Convenience', label: 'Stores' },
            { icon: VolunteerActivismRoundedIcon, value: 'Customer', label: 'Focused' },
          ].map(({ icon: Icon, value, label }) => (
            <Stack key={label} direction="row" spacing={0.8} alignItems="center" justifyContent="center" sx={{ px: { lg: 0.9, xl: 1.2 }, flex: 1 }}>
              <Icon sx={{ color: LOGIN_LIME, fontSize: { lg: 25, xl: 28 } }} />
              <Box>
                <Typography sx={{ fontWeight: 950, fontSize: value.length > 5 ? { lg: 13, xl: 14 } : { lg: 17, xl: 19 }, lineHeight: 1 }}>{value}</Typography>
                <Typography sx={{ mt: 0.3, color: 'rgba(255,255,255,.78)', fontSize: { lg: 10.5, xl: 11.5 } }}>{label}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
